import type { ChipMarkdownSpec, ChipRefAttrs } from "../rich-chip-editor";
import {
  MARKDOWN_MENTION_PATH_PREFIX,
  mentionLabelFromLinkText,
  mentionLinkMarkdown,
  parseMentionHref,
} from "./text";
import { inferMentionableType, type MentionableType } from "@quovy/types/chip-doc/mention-types";

const MENTION_LINK_PATTERN =
  /\[((?:\\.|[^\]])+)\]\((\/__mentions__\/(?:agent|user|tool)\/[^)]+|mention:[^)]+)\)/;

function mentionTypeOf(attrs: ChipRefAttrs): MentionableType {
  const type = attrs.refType;
  if (type === "agent" || type === "user" || type === "tool") return type;
  return inferMentionableType(attrs.id);
}

function chipifyMentionAnchors(root: HTMLElement): void {
  const doc = root.ownerDocument;
  for (const anchor of [...root.querySelectorAll("a[href]")]) {
    const href = anchor.getAttribute("href") ?? "";
    if (!href.startsWith(`${MARKDOWN_MENTION_PATH_PREFIX}/`) && !href.startsWith("mention:")) {
      continue;
    }
    const parsed = parseMentionHref(href);
    if (!parsed) continue;
    const label = mentionLabelFromLinkText(anchor.textContent ?? parsed.id);
    const span = doc.createElement("span");
    span.setAttribute("data-ref-id", parsed.id);
    span.setAttribute("data-ref-label", label);
    span.setAttribute("data-ref-type", parsed.type);
    span.textContent = label;
    anchor.replaceWith(span);
  }
}

/** Task-description mentions: markdown link out, `<a>` back to a chip on load. */
export const MENTION_MARKDOWN: ChipMarkdownSpec = {
  serialize: (attrs) => mentionLinkMarkdown(attrs.id, attrs.label, mentionTypeOf(attrs)),
  pattern: MENTION_LINK_PATTERN,
  attrs: (match) => {
    const parsed = parseMentionHref(match[2] ?? "");
    if (!parsed) return null;
    const label = mentionLabelFromLinkText(match[1] ?? parsed.id);
    return { id: parsed.id, label, refType: parsed.type };
  },
  updateDOM: chipifyMentionAnchors,
};
